import { useCallback, useMemo, useState } from 'react';
import { toast } from '@core/components';
import { useMenu } from '@core/hooks/useMenu';
import {
  type Order,
  type OrderItem,
  createOrderItem,
  deleteOrderItem,
  duplicateOrderItem,
  updateOrderItem,
} from '../../../api/order';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { VestmentType, getCategory } from '../../enum/vestmentType';
import { canEditOrder, isActiveProdOrder } from '../../utils/order';
import {
  ORDER_ITEM_REGISTRY,
  type OrderItemFormValues,
  buildOrderItemPayload,
  syncDesignWithCode,
} from '../../utils/orderItem';
import {
  clearOrderItemDraft,
  getAddItemInitialValues,
} from '../../utils/orderItem/draft';
import { getFormValuesCategory } from '../../utils/orderItem/helpers';
import type { OrderUpdatedHandler } from './useOrderDetailHandlers';

interface ItemDialogState {
  itemId: number | null;
  initialValues: OrderItemFormValues;
  expandedSection?: string;
}

function getItemFormValues(item: OrderItem): OrderItemFormValues | null {
  const type = item.vestment?.type;
  if (!type) {
    toast.error('Order item is missing vestment type.');
    return null;
  }

  return ORDER_ITEM_REGISTRY[getCategory(type)].apiToFormValues(item);
}

export function useOrderItemActions(
  order: Order,
  onUpdated?: OrderUpdatedHandler
) {
  const { vestments } = useAppContext();
  const [itemDialog, setItemDialog] = useState<ItemDialogState | undefined>(
    undefined
  );
  const {
    anchorEl: addMenuAnchor,
    isOpen: isAddMenuOpen,
    openMenu: openAddMenu,
    closeMenu: closeAddMenu,
  } = useMenu();
  const readOnly = !canEditOrder(order.status);

  const vestmentTypes = useMemo(
    () => new Map(vestments.map((v) => [String(v.id), v.type as VestmentType])),
    [vestments]
  );

  const closeItemDialog = useCallback(() => {
    setItemDialog(undefined);
  }, []);

  const openAddItemDialog = useCallback(
    (category: VestmentCategory) => {
      closeAddMenu();
      setItemDialog({
        itemId: null,
        initialValues: getAddItemInitialValues(
          category,
          order.id,
          order.customer.id
        ),
      });
    },
    [closeAddMenu, order.id, order.customer.id]
  );

  const openEditItemDialog = useCallback(
    (item: OrderItem, options?: { expandedSection?: string }) => {
      const formValues = getItemFormValues(item);
      if (!formValues) return;
      setItemDialog({
        itemId: item.id,
        initialValues: formValues,
        expandedSection: options?.expandedSection,
      });
    },
    []
  );

  const handleDuplicateItem = useCallback(
    async (item: OrderItem) => {
      try {
        const previousIds = new Set(order.items.map((existing) => existing.id));
        const updatedOrder = await duplicateOrderItem(order.id, item.id);
        toast.success(
          isActiveProdOrder(order.status)
            ? 'Item duplicated and queued for printing'
            : 'Item duplicated'
        );
        const duplicated = updatedOrder.items.find(
          (existing) => !previousIds.has(existing.id)
        );
        if (duplicated) {
          openEditItemDialog(duplicated, { expandedSection: 'body' });
        }
        await onUpdated?.(updatedOrder);
      } catch (error) {
        toast.apiError('Failed to duplicate item.', error);
      }
    },
    [openEditItemDialog, onUpdated, order]
  );

  const handleDeleteItem = useCallback(
    async (item: OrderItem) => {
      try {
        await deleteOrderItem(order.id, item.id);
        toast.success('Item deleted');
        await onUpdated?.();
      } catch (error) {
        toast.apiError('Failed to delete item.', error);
      }
    },
    [onUpdated, order.id]
  );

  const handleDialogSubmit = useCallback(
    async (data: OrderItemFormValues) => {
      if (!itemDialog) return;
      const synced = syncDesignWithCode(data);
      if (itemDialog.itemId !== null) {
        synced.id = itemDialog.itemId;
      }
      try {
        const payload = buildOrderItemPayload(synced, vestmentTypes);
        if (itemDialog.itemId === null) {
          await createOrderItem(order.id, payload);
          toast.success(
            isActiveProdOrder(order.status)
              ? 'Item created and queued for printing'
              : 'Item added'
          );
          const category = getFormValuesCategory(synced);
          if (category) {
            clearOrderItemDraft(order.id, category);
          }
        } else {
          await updateOrderItem(order.id, itemDialog.itemId, payload);
          toast.success('Item updated');
        }
        closeItemDialog();
        await onUpdated?.();
      } catch (error) {
        toast.apiError(
          itemDialog.itemId === null
            ? 'Failed to add item.'
            : 'Failed to update item.',
          error
        );
      }
    },
    [closeItemDialog, itemDialog, onUpdated, order, vestmentTypes]
  );

  return {
    readOnly,
    addMenuAnchor,
    isAddMenuOpen,
    openAddMenu,
    closeAddMenu,
    openAddItemDialog,
    itemDialog,
    closeItemDialog,
    openEditItemDialog,
    handleDuplicateItem,
    handleDeleteItem,
    handleDialogSubmit,
  };
}
